import { useCallback, useEffect, useState } from 'react';
import type {
  ApiClient, AussparungBaum as Baum, AussparungBaumKnoten,
  AussparungMedium,
} from '../api';
import { IconWarnung } from '../Icons';
import {
  ContentSection, DataTable, EmptyState, ErrorState, LoadingState,
  StatusBadge, type Spalte,
} from '../muster';
import {
  artname, bauartname, gewerkname, herkunftname, raumangabe,
  schottherkunftname,
} from '../aussparungKlartext';

/**
 * Die Aussparungen als Baum: Geschoss → Bauart → Aussparung → Medien.
 *
 * --- Warum eine Hierarchie und keine Liste ---------------------------------
 *
 * 5.668 Aussparungen in einer flachen Liste sind nicht prüfbar. Wer sie
 * abarbeitet, tut das geschossweise — und innerhalb eines Geschosses nach
 * Bauart, weil die Bauart das Schott bestimmt: Alle Trockenbauwände eines
 * Geschosses bekommen dasselbe System, und das prüft man einmal statt
 * vierhundertmal.
 *
 * --- Warum die Medien an der Aussparung hängen -----------------------------
 *
 * Durch eine Aussparung laufen oft mehrere Leitungen. Ein Kunststoffrohr
 * braucht eine Manschette, die im Brandfall zusammendrückt; ein Stahlrohr
 * daneben ein Wickelband. Genau diese Mehrzahl ist der Grund, warum eine
 * Aussparung mehr als ein Schott braucht — und warum eine Zeile je
 * Aussparung den Fall verschweigen würde.
 */

/**
 * Die Bauarten mit ihrer Farbe. Grau heißt: nicht bestimmt.
 *
 * Diese Hexwerte bleiben absichtlich stehen, während sonst auf dieser Seite
 * jede Farbe aus den Themenvariablen kommt: Sie sind eine KATEGORIALE Palette
 * und keine Zustandsfarbe. Sieben Bauarten müssen unterscheidbar sein, und
 * dafür gibt es in styles.css keine sieben Variablen - dort stehen gut,
 * warnung, schlecht und laufend, also Bedeutungen.
 *
 * Die eine Ausnahme mit Bedeutung ist Rot: Unterzug und Stütze dürfen im
 * Regelfall nicht durchbrochen werden.
 */
const BAUART_FARBE: Record<string, string> = {
  stahlbeton: '#64748b',
  mauerwerk: 'var(--warnung)',
  trockenbau: '#0891b2',
  tt_platte: '#7c3aed',
  unterzug: 'var(--schlecht)',
  stuetze: 'var(--schlecht)',
  holz: '#65a30d',
};

// Die Namen der Bauarten stehen in ../aussparungKlartext (bauartname) —
// ein Wörterbuch für alle vier Aussparungsseiten, das PDF und die
// LV-Ausgabe. Hier stand bis AS1 eine eigene Liste mit sechs Einträgen.

export function AussparungBaum({ api, projektId }: {
  api: ApiClient; projektId: string;
}) {
  const [baum, setBaum] = useState<Baum | null>(null);
  const [fehler, setFehler] = useState<unknown>(null);
  const [offen, setOffen] = useState<Set<string>>(new Set());
  const [gewaehlt, setGewaehlt] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);

  const laden = useCallback(() => {
    setFehler(null);
    api.aussparungBaum(projektId).then(setBaum).catch(setFehler);
  }, [api, projektId]);

  useEffect(() => { laden(); }, [laden]);

  const umschalten = (schluessel: string) => {
    setOffen((alt) => {
      const neu = new Set(alt);
      if (neu.has(schluessel)) neu.delete(schluessel);
      else neu.add(schluessel);
      return neu;
    });
  };

  const zuordnen = async () => {
    setLaeuft(true);
    try {
      await api.aussparungSchottZuordnen(projektId);
      laden();
    } catch (e) {
      setFehler(e);
    } finally {
      setLaeuft(false);
    }
  };

  if (fehler && !baum) {
    return (
      <ErrorState
        meldung="Der Baum lässt sich nicht laden."
        technisch={fehler}
        beiErneutVersuchen={laden}
      />
    );
  }

  if (!baum) {
    return <LoadingState text="Aussparungsbaum wird geladen …" zeilen={8} />;
  }

  // Die gewählte Aussparung, quer über alle Geschosse und Bauarten gesucht.
  const knoten = gewaehlt === null ? null
    : baum.geschosse
        .flatMap((g) => g.bauarten.flatMap((b) => b.aussparungen))
        .find((a) => a.id === gewaehlt) ?? null;

  return (
    <>
      <ContentSection
        titel="Aussparungen nach Geschoss und Bauart"
        beschreibung={'Die Bauart bestimmt das Schott: Conlit Pyrostat hat je ein '
          + 'Prüfzeugnis für Massivbau und Leichtbau. Rot markierte Bauarten — '
          + 'Unterzug und Stütze — dürfen im Regelfall NICHT durchbrochen werden, '
          + 'jedenfalls nicht ohne den Tragwerksplaner.'}
        meta={`${baum.gesamt.toLocaleString('de-DE')} Aussparungen`}
        aktionen={(
          <button type="button" className="m-knopf" onClick={zuordnen} disabled={laeuft}>
            {laeuft ? 'ordnet zu …' : 'Brandschotts neu zuordnen'}
          </button>
        )}
        nackt
      >
        {fehler !== null && (
          <ErrorState klein meldung="Die Zuordnung ist fehlgeschlagen."
                      technisch={fehler} />
        )}
      </ContentSection>

      {/* Links die Gliederung, rechts die gewählte Aussparung - dasselbe
          Muster wie Heizung und Kälte.

          Vorher war es ein vierstufiges Akkordeon: Die Medientabelle mit acht
          Spalten stand dreifach eingerückt unter ihrer Aussparung und war
          dort nicht lesbar. Rechts hat sie die ganze Breite. */}
      <div className="karte" style={{ display: 'flex', gap: 0, padding: 0 }}>
        <div className="baum">

      {baum.geschosse.map((g) => (
            <div key={g.geschoss}>
              <button className="baum-ebene"
                      onClick={() => umschalten(g.geschoss)}>
                <span>{g.geschoss}</span>
                <span className="schwach">
                  {g.anzahl.toLocaleString('de-DE')}
                </span>
              </button>

              {offen.has(g.geschoss) && g.bauarten.map((b) => {
                const bSchluessel = `${g.geschoss}|${b.bauart}`;
                const farbe = BAUART_FARBE[b.bauart] ?? 'var(--text-schwach)';

                return (
                  <div key={b.bauart}>
                    <button
                      className="baum-zeile"
                      style={{ paddingLeft: 22 }}
                      onClick={() => umschalten(bSchluessel)}
                    >
                      {/* Der Farbstreifen bleibt: Unterzug und Stütze sind
                          rot, und das ist die eine Farbe auf dieser Seite,
                          die eine fachliche Aussage trägt. */}
                      <span style={{ width: 3, alignSelf: 'stretch',
                                     background: farbe, borderRadius: 2 }} />
                      <span style={{ color: farbe, fontWeight: 600,
                                     minWidth: 0, overflow: 'hidden',
                                     textOverflow: 'ellipsis',
                                     whiteSpace: 'nowrap' }}>
                        {bauartname(b.bauart)}
                      </span>
                      <span className="schwach">
                        {b.anzahl.toLocaleString('de-DE')}
                      </span>
                    </button>

                    {offen.has(bSchluessel) && (
                      <>
                        {b.aussparungen.slice(0, 200).map((a) => (
                          <button
                            key={a.id}
                            className={`baum-zeile ${
                              gewaehlt === a.id ? 'aktiv' : ''}`}
                            style={{ paddingLeft: 40 }}
                            onClick={() => setGewaehlt(a.id)}
                            title={a.kennzeichen
                                   ?? String(a.revitElementId)}
                          >
                            <span style={{ minWidth: 0, overflow: 'hidden',
                                           textOverflow: 'ellipsis',
                                           whiteSpace: 'nowrap' }}>
                              {a.kennzeichen ?? a.revitElementId}
                            </span>

                            <span style={{ marginLeft: 'auto', display: 'flex',
                                           gap: 4, fontSize: 10 }}>
                              {a.medien.length > 0 && (
                                <span className="schwach"
                                      title={`${a.medien.length} Medium/Medien`}>
                                  {a.medien.length}
                                </span>
                              )}
                              {!a.anforderungGeprueft && (
                                <IconWarnung size={12} />
                              )}
                            </span>
                          </button>
                        ))}

                        {b.aussparungen.length > 200 && (
                          <div className="sehr-schwach"
                               style={{ fontSize: 11, padding: '4px 12px 4px 40px' }}>
                            Die ersten 200 von{' '}
                            {b.aussparungen.length.toLocaleString('de-DE')}.
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
      ))}
        </div>

        <div style={{ flex: 1, padding: '12px 16px', minWidth: 0 }}>
          {knoten
            ? <Knoten a={knoten} />
            : (
              <EmptyState
                titel="Keine Aussparung gewählt."
                erklaerung={'Links ein Geschoss aufklappen, dann eine Bauart und eine '
                  + 'Aussparung wählen — ihre Medien erscheinen dann hier.'}
              />
            )}
        </div>
      </div>
    </>
  );
}

/**
 * Eine einzelne Aussparung mit ihren Bauteilen und Medien.
 *
 * Steht rechts und nicht mehr eingeruecktet im Baum: Die Medientabelle hat
 * acht Spalten, und dreifach eingerueckt war sie nicht lesbar. Sie braucht
 * kein Aufklappen mehr - was gewaehlt ist, ist offen.
 */
function Knoten({ a }: { a: AussparungBaumKnoten }) {
  const masse = a.durchmesserMm
    ? `⌀ ${z(a.durchmesserMm, 0)}`
    : `${z(a.breiteMm, 0)} × ${z(a.hoeheMm, 0)}`;

  // Von Raum nach Raum. Fehlt eine Seite, steht dort ein Strich - das ist
  // eine Aussage: Durchbruch in einen Schacht, ins Freie oder ins Erdreich.
  const a_ = a.raeume.find((r) => r.seite === 'a');
  const b_ = a.raeume.find((r) => r.seite === 'b');

  // Die Raumnummer EINMAL: 9.629 von 9.630 `raumname` enden selbst auf die
  // Nummer, und „E1-176 Küche Musicland Süd E1-176" stand bis AS1 hier wie
  // auf dem Blatt. Dieselbe Funktion wie im PDF.
  const raumtext = (r?: { raumnummer: string | null;
                          raumname: string | null }) =>
    raumangabe(r?.raumnummer, r?.raumname);

  return (
    <>
      <h3 style={{ marginTop: 0, marginBottom: 2 }}>
        {a.kennzeichen ?? a.revitElementId}
      </h3>

      <div className="schwach" style={{ fontSize: 12, marginBottom: 12 }}>
        {masse} mm · {raumtext(a_)} → {raumtext(b_)}
      </div>

      <div style={{ fontSize: 12 }}>
          {/* Die durchbrochenen Bauteile */}
          <div style={{ marginBottom: 6 }}>
            <span className="schwach">Durchbrochenes Bauteil: </span>
            {a.bauteile.length === 0 ? (
              <em style={{ color: 'var(--warnung)' }}>
                keines gefunden — ohne Bauart ist kein Schott wählbar
              </em>
            ) : (
              a.bauteile.map((h, i) => (
                <span key={i}>
                  {i > 0 && ', '}
                  {h.typ ?? h.kategorie}
                  {h.dickeMm && ` (${z(h.dickeMm, 0)} mm)`}
                  {h.bauartHerkunft === 'kategorie' && (
                    <span className="schwach"
                          title={herkunftname(h.bauartHerkunft)}>
                      {' '}~
                    </span>
                  )}
                </span>
              ))
            )}
          </div>

          {/* Die Anforderung */}
          <div style={{ marginBottom: 6 }}>
            <span className="schwach">Anforderung: </span>
            {a.anforderungGeprueft ? (
              <strong>{a.anforderungGeprueft}</strong>
            ) : (
              <em style={{ color: 'var(--warnung)' }}>
                nicht geprüft — im Modell steht
                {' '}„{a.anforderungRoh ?? 'nichts'}"
              </em>
            )}
          </div>

          {/* Die Medien mit ihrem Schott */}
          {a.medien.length === 0 ? (
            <EmptyState
              klein
              titel="Kein Medium."
              erklaerung={'Entweder Reserve — dann gehört die Aussparung verschlossen — '
                + 'oder das Fachmodell fehlt.'}
            />
          ) : (
            <DataTable
              name="Medien und ihr Schott"
              spalten={MEDIENSPALTEN}
              zeilen={a.medien}
              schluessel={(m) => `${m.art}|${m.gewerk}|${m.nennweite}|${m.typ}`}
              suche={a.medien.length > 8}
              spaltenwahl={false}
              dicht
            />
          )}
      </div>
    </>
  );
}

/** Die Spalten der Medientabelle — je Aussparung dieselben. */
const MEDIENSPALTEN: Spalte<AussparungMedium>[] = [
  {
    schluessel: 'medium', titel: 'Medium', immerSichtbar: true, breite: '120px',
    wert: (m) => artname(m.art),
    inhalt: (m) => artname(m.art),
  },
  {
    schluessel: 'gewerk', titel: 'Gewerk', breite: '110px',
    wert: (m) => gewerkname(m.gewerk),
    inhalt: (m) => (m.gewerk
      ? gewerkname(m.gewerk)
      : <em className="schwach">offen</em>),
  },
  {
    schluessel: 'system', titel: 'System',
    wert: (m) => m.systemtyp ?? m.typ ?? '',
    inhalt: (m) => <span className="schwach">{m.systemtyp ?? m.typ ?? '—'}</span>,
  },
  {
    schluessel: 'mass', titel: 'DN / B×H', ausrichtung: 'rechts', breite: '120px',
    wert: (m) => m.nennweite ?? Math.max(m.breiteMm ?? 0, m.hoeheMm ?? 0),
    inhalt: (m) => (m.nennweite
      ? `DN ${m.nennweite}`
      : m.breiteMm && m.hoeheMm
        ? `${z(m.breiteMm, 0)}×${z(m.hoeheMm, 0)}`
        : '—'),
  },
  {
    schluessel: 'werkstoff', titel: 'Werkstoff', breite: '150px',
    wert: (m) => m.werkstoff ?? '',
    inhalt: (m) => (
      <span className="schwach">
        {m.werkstoff ?? '—'}
        {m.istBrennbar === true && (
          <> <StatusBadge zustand="warnung" text="brennbar" klein
                          titel="braucht eine Manschette" /></>
        )}
      </span>
    ),
  },
  {
    schluessel: 'schott', titel: 'Brandschott',
    wert: (m) => m.schott?.system ?? '',
    inhalt: (m) => (
      <>
        {m.schott?.system ?? <em className="schwach">keines</em>}
        {m.schott?.hersteller && (
          <span className="schwach" style={{ fontSize: 11 }}>
            {' '}({m.schott.hersteller})
          </span>
        )}
      </>
    ),
  },
  {
    schluessel: 'nummer', titel: 'Nummer', breite: '190px',
    wert: (m) => m.nummer?.nummer ?? '',
    inhalt: (m) => (m.nummer ? (
      <span style={{ fontFamily: 'monospace', fontSize: 11, fontWeight: 600 }}
            title={`vergeben am ${m.nummer.vergebenAm?.slice(0, 10)}`}>
        {m.nummer.nummer}
      </span>
    ) : (
      <em className="schwach" style={{ fontSize: 11 }}
          title={'Eine Nummer bekommt nur, was vollständig bestimmt ist — sie steht '
            + 'danach im Modell und auf der Baustelle'}>
        offen
      </em>
    )),
  },
  {
    schluessel: 'zulassung', titel: 'Zulassung', art: 'status', breite: '180px',
    wert: (m) => m.schott?.zulassungNummer ?? '',
    inhalt: (m) => {
      const s2 = m.schott;
      if (!s2?.zulassungNummer) return <em className="schwach">—</em>;
      return (
        <StatusBadge
          klein
          zustand={s2.herkunft === 'gerechnet' ? 'gut' : 'warnung'}
          text={s2.zulassungNummer}
          // Die Herkunft im Klartext VOR dem Befund: 'geprueft_ohne' heißt
          // „geprüft, ohne Schott" und nicht „geprüft".
          titel={`${schottherkunftname(s2.herkunft)}${
            s2.befund ? ` — ${s2.befund}` : ''}`}
        />
      );
    },
  },
];

// Die Namen der Durchgangsarten stehen in ../aussparungKlartext (artname).

function z(x: number | null | undefined, n = 1): string {
  return x == null ? '—' : x.toLocaleString('de-DE', {
    minimumFractionDigits: n, maximumFractionDigits: n });
}
